import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';
import { CASEPACK_PROPERTY, DELIVERY_DATE_PROPERTY, readDeliveryDate } from '@theme/casepack';

/**
 * @module delivery-date
 *
 * Split shipments for the Milio Milano wholesale cart.
 *
 * Every cart row has a selection checkbox (`.delivery-select__input`). The
 * scheduler bar above the rows (`<delivery-scheduler>`) moves the selected
 * rows into a delivery date, or back to "ship now". The date lives on the
 * visible `Delivery date` line-item property (ISO `YYYY-MM-DD`), so it carries
 * through checkout onto the order. A missing date means the line ships now.
 * The cart Liquid groups rows by date into shipments.
 *
 * A collapsed casepack row stands for several real per-size lines; all of
 * them move together.
 *
 * The module also keeps a cart attribute (`Delivery schedule`) in sync with
 * the lines, e.g. `Ship now (24 units); 2026-10-15 (12 units)`, so the split is
 * visible at order level. It is cleared when everything ships now.
 *
 * The checkboxes and shipment-header buttons sit inside `cart-items-component`
 * rows, so they carry no `ref`/`on:` attributes (those would re-scope the
 * cart's own refs). The scheduler listens to them by delegation instead.
 */

export const DELIVERY_SCHEDULE_ATTRIBUTE = 'Delivery schedule';

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const CHECKBOX_SELECTOR = '.delivery-select__input';
const SHIP_NOW_GROUP_SELECTOR = '[data-delivery-ship-now]';

/**
 * Line properties as a plain object, whichever shape Shopify returned.
 * @param {any} line
 * @returns {Record<string, string>}
 */
function lineProperties(line) {
  const props = line && line.properties;
  if (!props) return {};
  if (Array.isArray(props)) {
    return Object.fromEntries(props.filter((entry) => Array.isArray(entry) && entry.length === 2));
  }
  return { ...props };
}

/**
 * Properties for a line moved to `date` ('' removes the date).
 * @param {any} line
 * @param {string} date
 * @returns {Record<string, string>}
 */
function propertiesWithDate(line, date) {
  const properties = lineProperties(line);
  if (date) properties[DELIVERY_DATE_PROPERTY] = date;
  else delete properties[DELIVERY_DATE_PROPERTY];
  return properties;
}

/**
 * Order-level summary of the shipments in a cart. Returns '' when every line
 * ships now (nothing to split).
 * @param {any[]} items `cart.items` from `/cart.js`.
 * @returns {string}
 */
export function buildDeliverySchedule(items) {
  /** @type {Map<string, number>} */
  const units = new Map();
  for (const line of items || []) {
    const date = readDeliveryDate(line);
    units.set(date, (units.get(date) || 0) + (Number(line.quantity) || 0));
  }

  if (units.size === 0 || (units.size === 1 && units.has(''))) return '';

  return [...units.keys()]
    .sort((a, b) => (a === '' ? -1 : b === '' ? 1 : a.localeCompare(b)))
    .map((date) => `${date || 'Ship now'} (${units.get(date)} units)`)
    .join('; ');
}

/** @returns {Promise<any>} The current cart from `/cart.js`. */
async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
  return response.json();
}

/**
 * POST a JSON payload to a cart route; throws on Shopify cart errors.
 * @param {string} url
 * @param {object} payload
 * @returns {Promise<any>}
 */
async function cartPost(url, payload) {
  const response = await fetch(url, fetchConfig('json', { body: JSON.stringify(payload) }));
  const data = await response.json();
  if (!response.ok || typeof data?.status === 'number' || data?.errors) {
    const error = new Error(data?.description || data?.message || data?.errors || 'Cart request failed');
    // @ts-expect-error attach the Shopify error body for callers to inspect.
    error.cartError = data;
    throw error;
  }
  return data;
}

/**
 * Move cart lines to a delivery date ('' = ship now).
 *
 * Fast path: add the lines again with the new date in one `/cart/add.js`
 * call, then zero the old lines in one `/cart/update.js` call. Nothing is
 * removed unless the add succeeded. If the add is refused (for example
 * inventory is too tight to briefly hold both copies), fall back to rewriting
 * each line in place with `/cart/change.js`, one request per line.
 *
 * @param {any[]} lines Lines from `/cart.js`.
 * @param {string} date
 * @param {(done: number, total: number) => void} [onProgress]
 */
export async function moveLinesToDate(lines, date, onProgress) {
  const toMove = lines.filter((line) => readDeliveryDate(line) !== date);
  if (toMove.length === 0) return;

  let added = false;
  try {
    await cartPost(Theme.routes.cart_add_url, {
      items: toMove.map((line) => ({
        id: Number(line.variant_id),
        quantity: Number(line.quantity),
        properties: propertiesWithDate(line, date),
      })),
    });
    added = true;
  } catch (error) {
    console.info('[delivery-date] bulk move refused, updating lines one by one:', error);
  }

  if (added) {
    /** @type {Record<string, number>} */
    const updates = {};
    for (const line of toMove) updates[String(line.key)] = 0;
    await cartPost(Theme.routes.cart_update_url, { updates });
    onProgress?.(toMove.length, toMove.length);
    return;
  }

  // Line keys derive from properties, so each write only changes the key of
  // the line it targets; the remaining keys stay valid.
  let done = 0;
  for (const line of toMove) {
    await cartPost(Theme.routes.cart_change_url, {
      id: String(line.key),
      quantity: Number(line.quantity),
      properties: propertiesWithDate(line, date),
    });
    done++;
    onProgress?.(done, toMove.length);
  }
}

/**
 * Write the `Delivery schedule` cart attribute when it no longer matches the
 * lines. With `sections`, always writes and returns the section HTML.
 * @param {any} cart
 * @param {{ sections?: string }} [opts]
 * @returns {Promise<any | null>}
 */
export async function syncDeliverySchedule(cart, opts = {}) {
  const schedule = buildDeliverySchedule(cart.items);
  const current = cart.attributes?.[DELIVERY_SCHEDULE_ATTRIBUTE] || '';
  if (schedule === current && !opts.sections) return null;

  /** @type {Record<string, unknown>} */
  const payload = { attributes: { [DELIVERY_SCHEDULE_ATTRIBUTE]: schedule } };
  if (opts.sections) payload.sections = opts.sections;
  return cartPost(Theme.routes.cart_update_url, payload);
}

/** @returns {string} Comma-separated ids of every cart section on the page. */
function cartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((element) => /** @type {HTMLElement} */ (element).dataset.sectionId)
    .filter(Boolean)
    .join(',');
}

/**
 * The cart lines a row checkbox stands for.
 * @param {HTMLElement} checkbox
 * @param {any[]} items
 * @returns {any[]}
 */
function linesForRow(checkbox, items) {
  const current = checkbox.dataset.deliveryDate || '';
  const casepackKey = checkbox.dataset.casepackKey;

  if (casepackKey) {
    return items.filter(
      (line) =>
        String(lineProperties(line)[CASEPACK_PROPERTY] ?? '') === casepackKey && readDeliveryDate(line) === current
    );
  }

  const lineKey = checkbox.dataset.lineKey;
  return items.filter((line) => line.key === lineKey);
}

/**
 * Selection toolbar that schedules the checked cart rows.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} selectAll
 * @property {HTMLElement} count
 * @property {HTMLButtonElement} scheduleButton
 * @property {HTMLButtonElement} shipNowButton
 * @property {HTMLElement} panel
 * @property {HTMLInputElement} dateInput
 * @property {HTMLButtonElement} applyButton
 * @property {HTMLElement} status
 * @property {HTMLButtonElement[]} [existingDates]
 * @extends {Component<Refs>}
 */
class DeliveryScheduler extends Component {
  requiredRefs = ['selectAll', 'count', 'scheduleButton', 'shipNowButton', 'panel', 'dateInput', 'applyButton', 'status'];

  /** Row ids the customer has checked; survives cart re-renders. */
  #selected = new Set();
  #busy = false;
  /** @type {HTMLElement | null} */
  #container = null;
  /** @type {MutationObserver | null} */
  #observer = null;

  connectedCallback() {
    super.connectedCallback();

    this.#container = this.closest('.cart-items') ?? this.parentElement;
    this.#container?.addEventListener('change', this.#onContainerChange);
    this.#container?.addEventListener('click', this.#onContainerClick);

    // Cart re-renders reset the checkboxes; put the selection back.
    this.#observer = new MutationObserver(() => this.#restoreSelection());
    if (this.#container) this.#observer.observe(this.#container, { childList: true, subtree: true });

    this.#restoreSelection();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#container?.removeEventListener('change', this.#onContainerChange);
    this.#container?.removeEventListener('click', this.#onContainerClick);
    this.#observer?.disconnect();
    this.#observer = null;
    this.#container = null;
  }

  /** @returns {HTMLInputElement[]} */
  get #checkboxes() {
    if (!this.#container) return [];
    return /** @type {HTMLInputElement[]} */ (Array.from(this.#container.querySelectorAll(CHECKBOX_SELECTOR)));
  }

  /** @param {Event} event */
  #onContainerChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.matches(CHECKBOX_SELECTOR)) return;
    const id = target.dataset.rowId;
    if (!id) return;
    if (target.checked) this.#selected.add(id);
    else this.#selected.delete(id);
    this.#render();
  };

  /** @param {Event} event */
  #onContainerClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest(SHIP_NOW_GROUP_SELECTOR) : null;
    if (!(target instanceof HTMLElement)) return;
    event.preventDefault();
    const date = target.dataset.deliveryShipNow || '';
    if (!date) return;
    const rows = this.#checkboxes.filter((checkbox) => (checkbox.dataset.deliveryDate || '') === date);
    this.#move(rows, '');
  };

  toggleAll() {
    const checked = this.refs.selectAll.checked;
    this.#selected.clear();
    for (const checkbox of this.#checkboxes) {
      checkbox.checked = checked;
      if (checked && checkbox.dataset.rowId) this.#selected.add(checkbox.dataset.rowId);
    }
    this.#render();
  }

  openSchedule() {
    if (this.#selectedCheckboxes().length === 0) return;
    this.refs.panel.hidden = false;
    this.refs.scheduleButton.setAttribute('aria-expanded', 'true');
    this.#setStatus('');
    this.refs.dateInput.focus();
    this.#render();
  }

  cancelSchedule() {
    this.#closePanel();
    this.refs.scheduleButton.focus();
  }

  onDateInput() {
    this.#render();
  }

  /** @param {Event} event */
  useExistingDate(event) {
    const button = event.target instanceof Element ? event.target.closest('[data-date]') : null;
    const date = button instanceof HTMLElement ? button.dataset.date || '' : '';
    if (!ISO_DATE.test(date)) return;
    this.refs.dateInput.value = date;
    this.applySchedule();
  }

  applySchedule() {
    const { dateInput } = this.refs;
    const date = dateInput.value;
    if (!ISO_DATE.test(date)) return;
    if (dateInput.min && date < dateInput.min) {
      this.#setStatus(this.dataset.tooEarlyMessage || '', true);
      return;
    }
    this.#move(this.#selectedCheckboxes(), date);
  }

  shipNowSelected() {
    const rows = this.#selectedCheckboxes().filter((checkbox) => checkbox.dataset.deliveryDate);
    this.#move(rows, '');
  }

  /** @returns {HTMLInputElement[]} */
  #selectedCheckboxes() {
    return this.#checkboxes.filter((checkbox) => checkbox.checked);
  }

  #closePanel() {
    this.refs.panel.hidden = true;
    this.refs.scheduleButton.setAttribute('aria-expanded', 'false');
    this.refs.dateInput.value = '';
    this.#render();
  }

  #restoreSelection() {
    const checkboxes = this.#checkboxes;
    const present = new Set();
    for (const checkbox of checkboxes) {
      const id = checkbox.dataset.rowId;
      if (!id) continue;
      present.add(id);
      const shouldCheck = this.#selected.has(id);
      if (checkbox.checked !== shouldCheck) checkbox.checked = shouldCheck;
    }
    for (const id of [...this.#selected]) {
      if (!present.has(id)) this.#selected.delete(id);
    }
    this.#render();
  }

  #render() {
    const { selectAll, count, scheduleButton, shipNowButton, panel, dateInput, applyButton } = this.refs;
    if (!count) return;

    const checkboxes = this.#checkboxes;
    const selected = checkboxes.filter((checkbox) => checkbox.checked);
    const hasScheduled = selected.some((checkbox) => checkbox.dataset.deliveryDate);

    const text = selected.length
      ? (selected.length === 1 ? this.dataset.selectedOne : this.dataset.selectedOther || '').replace(
          '[count]',
          String(selected.length)
        )
      : this.dataset.noneSelected || '';
    if (count.textContent !== text) count.textContent = text;

    const allChecked = checkboxes.length > 0 && selected.length === checkboxes.length;
    const someChecked = selected.length > 0 && !allChecked;
    if (selectAll.checked !== allChecked) selectAll.checked = allChecked;
    if (selectAll.indeterminate !== someChecked) selectAll.indeterminate = someChecked;

    scheduleButton.disabled = this.#busy || selected.length === 0;
    shipNowButton.disabled = this.#busy || !hasScheduled;
    applyButton.disabled = this.#busy || selected.length === 0 || !ISO_DATE.test(dateInput.value);
    for (const button of this.refs.existingDates ?? []) {
      button.disabled = this.#busy || selected.length === 0;
    }
    if (selected.length === 0 && !panel.hidden) {
      panel.hidden = true;
      scheduleButton.setAttribute('aria-expanded', 'false');
    }
    this.classList.toggle('delivery-scheduler--busy', this.#busy);
  }

  /**
   * @param {string} message
   * @param {boolean} [isError]
   */
  #setStatus(message, isError = false) {
    const { status } = this.refs;
    if (status.textContent !== message) status.textContent = message;
    status.classList.toggle('delivery-scheduler__status--error', isError);
  }

  /**
   * Move the cart lines behind `rows` to `date` and re-render the cart.
   * @param {HTMLInputElement[]} rows
   * @param {string} date ISO date, or '' to ship now.
   */
  async #move(rows, date) {
    if (this.#busy || rows.length === 0) return;
    this.#busy = true;
    this.#render();
    this.#setStatus(this.dataset.updatingMessage || '');

    const sections = cartSectionIds();
    const deferred = CartLinesUpdateEvent.createPromise();
    let dispatched = false;

    try {
      const cart = await fetchCart();
      /** @type {Map<string, any>} */
      const lines = new Map();
      for (const row of rows) {
        for (const line of linesForRow(row, cart.items)) lines.set(String(line.key), line);
      }
      if (lines.size === 0) throw new Error('Cart lines for the selected rows were not found');

      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'update',
          context: 'cart',
          lines: [...lines.values()].map((line) => ({ id: String(line.key), quantity: Number(line.quantity) })),
          promise: deferred.promise,
        })
      );
      dispatched = true;

      await moveLinesToDate([...lines.values()], date, (done, total) => {
        if (total > 1 && done < total) {
          this.#setStatus(`${this.dataset.updatingMessage || ''} ${done}/${total}`);
        }
      });

      const updated = await syncDeliverySchedule(await fetchCart(), { sections });

      // The moved rows get new ids; start the next selection fresh.
      this.#selected.clear();
      this.refs.panel.hidden = true;
      this.refs.scheduleButton.setAttribute('aria-expanded', 'false');
      this.refs.dateInput.value = '';
      this.#setStatus('');

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(updated),
        detail: {
          sections: updated.sections,
          items: updated.items,
          itemCount: updated.item_count,
          source: 'delivery-scheduler',
          didError: false,
        },
      });
    } catch (error) {
      console.warn('[delivery-date] could not move items:', error);
      if (dispatched) deferred.reject(error);

      const message = this.dataset.errorMessage || 'Could not update the delivery date.';
      this.dispatchEvent(
        new CartErrorEvent({
          error: message,
          code: /** @type {any} */ (error)?.cartError ? 'INVALID' : 'SERVICE_UNAVAILABLE',
        })
      );
      await this.#refreshCart();
      this.#setStatus(message, true);
    } finally {
      this.#busy = false;
      this.#render();
    }
  }

  /** Re-render the cart sections so they show the real state after an error. */
  async #refreshCart() {
    try {
      const ids = cartSectionIds().split(',').filter(Boolean);
      await Promise.all(
        ids.map((id) => {
          const component = document.querySelector(`cart-items-component[data-section-id="${id}"]`);
          const isDrawer = component instanceof HTMLElement && component.dataset.drawer !== undefined;
          return sectionRenderer.renderSection(id, { cache: false, mode: isDrawer ? 'hydration' : 'full' });
        })
      );
    } catch (error) {
      console.warn('[delivery-date] cart refresh failed:', error);
    }
  }
}

if (!customElements.get('delivery-scheduler')) {
  customElements.define('delivery-scheduler', DeliveryScheduler);
}

/*
 * Keep the order-level schedule attribute honest when lines change through
 * other controls (quantity, remove, add to cart). Only runs when the cart has
 * a scheduled row or already carries a schedule, so carts that ship
 * everything now never pay for the extra request.
 */
if (!(/** @type {any} */ (window).__deliveryScheduleSync)) {
  /** @type {any} */ (window).__deliveryScheduleSync = true;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let syncTimer;

  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    /** @type {any} */ (event).promise
      ?.then((/** @type {any} */ result) => {
        if (result?.detail?.source === 'delivery-scheduler') return;

        clearTimeout(syncTimer);
        syncTimer = setTimeout(async () => {
          const hasScheduledRow = document.querySelector(`${CHECKBOX_SELECTOR}[data-delivery-date]:not([data-delivery-date=""])`);
          const hasSchedule = document.querySelector('[data-delivery-schedule]:not([data-delivery-schedule=""])');
          if (!hasScheduledRow && !hasSchedule) return;

          try {
            await syncDeliverySchedule(await fetchCart());
          } catch (error) {
            console.warn('[delivery-date] schedule sync failed:', error);
          }
        }, 600);
      })
      .catch(() => {});
  });
}
